import type { SiteChangeEntity } from '@lib/enums/site-change.enum'
import type { IrrigationEntity } from '@lib/input/irrigation.input'
import type { IrrigationEquipmentKind } from '@lib/enums/irrigation.enum'
import type { IrrigationChange } from '@/irrigation-survey/irrigationOperations'
import {
  editQuestion,
  equipmentSnapshot,
  timerSnapshot,
  waterSourceSnapshot,
  zoneSnapshot,
  type ChangeQuestion,
} from '@/site-visits/siteRecordChanges'

/*
 * "Was it wrong, or did it change?" for the irrigation system, which the
 * survey asks on a revisit (docs/site-visits.md).
 */

/** The part of the site record an entity is, for its history; null for a problem, which is a finding. */
export const TRACKED: Record<IrrigationEntity, SiteChangeEntity | null> = {
  waterSource: 'WATER_SOURCE',
  timer: 'TIMER',
  zone: 'ZONE',
  equipment: 'EQUIPMENT',
  problem: null,
}

/**
 * What saving an edit asks on a revisit: none when nothing changed could be
 * either, and the wording for what did. Read off the changes the sheet
 * would make, which say which kind of record they are.
 */
export function editAsks(
  before: IrrigationChange,
  after: IrrigationChange,
): ChangeQuestion | null {
  if (before.kind !== 'upsert' || after.kind !== 'upsert') return null
  if (before.entity === 'waterSource' && after.entity === 'waterSource') {
    return editQuestion(
      'WATER_SOURCE',
      waterSourceSnapshot(before.record),
      waterSourceSnapshot(after.record),
    )
  }
  if (before.entity === 'timer' && after.entity === 'timer') {
    return editQuestion(
      'TIMER',
      timerSnapshot(before.record),
      timerSnapshot(after.record),
    )
  }
  if (before.entity === 'zone' && after.entity === 'zone') {
    return editQuestion(
      'ZONE',
      zoneSnapshot(before.record),
      zoneSnapshot(after.record),
    )
  }
  if (before.entity === 'equipment' && after.entity === 'equipment') {
    return editQuestion(
      'EQUIPMENT',
      equipmentSnapshot(before.record),
      equipmentSnapshot(after.record),
    )
  }
  return null
}

/**
 * The part of the site record a dropped pin makes, when dropping it saves it
 * at once and so asks on the spot: null for a problem (a finding) and for
 * "something else", whose sheet asks when its name is saved.
 */
export function placedRecord(
  kind: 'waterSource' | 'timer' | 'problem' | IrrigationEquipmentKind,
): SiteChangeEntity | null {
  switch (kind) {
    case 'waterSource':
      return 'WATER_SOURCE'
    case 'timer':
      return 'TIMER'
    case 'problem':
    case 'OTHER':
      return null
    default:
      return 'EQUIPMENT'
  }
}
